import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, Paper, TableHead, TableRow,
  TableSortLabel, Collapse, IconButton, Box, Typography, LinearProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from 'components/mui';
import { AddIcon, RemoveIcon, DeleteIcon } from 'components/icons';
import { useKeywordList } from 'containers/pages/keywordManagement/hooks';
import { getComparator, stableSort } from 'libs/sortList';
import KeywordForm from './KeywordForm';
import { Keyword, KeywordFormData } from 'containers/pages/keywordManagement/types';

const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100;

interface KeywordListProps {
  keywords: Keyword[];
  onSave: (formData: KeywordFormData, isEdit: boolean) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

function KeywordList({ keywords, onSave, onDelete }: KeywordListProps) {
  const { state, handler } = useKeywordList();
  const listRef = useRef<HTMLDivElement>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortedKeywords = useMemo(() => stableSort(keywords, getComparator(state.order, state.orderBy) as any) as Keyword[], [keywords, state.order, state.orderBy]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => { setVisibleCount(INITIAL_VISIBLE_COUNT); }, [sortedKeywords]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let sc: HTMLElement | null = el.parentElement;
    while (sc) {
      const { overflow, overflowY } = getComputedStyle(sc);
      if (['auto', 'scroll'].includes(overflow) || ['auto', 'scroll'].includes(overflowY)) break;
      sc = sc.parentElement;
    }
    if (!sc) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = sc!;
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD && !isLoadingMore && visibleCount < sortedKeywords.length) {
        setIsLoadingMore(true);
        setTimeout(() => { setVisibleCount((p) => Math.min(p + LOAD_MORE_COUNT, sortedKeywords.length)); setIsLoadingMore(false); }, 100);
      }
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc!.removeEventListener('scroll', onScroll);
  }, [isLoadingMore, visibleCount, sortedKeywords.length]);

  const visibleRows = useMemo(() => sortedKeywords.slice(0, visibleCount), [sortedKeywords, visibleCount]);
  const totalCols = state.headTableCells.length;

  const onConfirmDelete = async () => {
    if (deleteId !== null) { await onDelete(deleteId); setDeleteId(null); }
  };

  return (
    <Paper ref={listRef} elevation={1}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Showing {visibleRows.length} of {sortedKeywords.length} keywords
        </Typography>
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {state.headTableCells.map((cell) => {
                const isActive = state.orderBy === cell.property;
                return (
                  <TableCell key={cell.id} sortDirection={isActive ? state.order : false} sx={cell.width ? { width: cell.width } : undefined}>
                    {cell.property ? (<TableSortLabel active={isActive} direction={isActive ? state.order : 'asc'} onClick={handler.onSort(cell.property)}>{cell.label}</TableSortLabel>) : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={totalCols} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No keywords found</Typography>
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map((keyword) => {
              const isOpen = state.selectedIds.includes(keyword.Id);
              return (
                <React.Fragment key={keyword.Id}>
                  <TableRow hover onClick={() => handler.onToggleExpand(keyword.Id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <IconButton aria-label={isOpen ? 'Collapse' : 'Expand'} aria-expanded={isOpen} size="small">
                        {isOpen ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{keyword.Title}</TableCell>
                    <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>{keyword.Instructions || ''}</Typography></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ p: 0, overflowX: 'hidden', maxWidth: '100%' }} colSpan={totalCols}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <IconButton color="error" onClick={(e) => { e.stopPropagation(); setDeleteId(keyword.Id); }} size="small">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          <KeywordForm mode="edit" keyword={keyword} onSave={onSave} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {isLoadingMore && (
              <TableRow>
                <TableCell colSpan={totalCols}><Box sx={{ width: '100%', py: 1 }}><LinearProgress /></Box></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ p: 1, textAlign: 'center' }}>
        {!isLoadingMore && visibleRows.length >= sortedKeywords.length && sortedKeywords.length > 0 && (
          <Typography variant="caption" color="text.secondary">End of list</Typography>
        )}
      </Box>
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this keyword? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={onConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default KeywordList;
